import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Tabs,
  Tab
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Verified as VerifiedIcon,
  CheckCircle as CheckCircleIcon,
  Accessibility as AccessibilityIcon,
  Keyboard as KeyboardIcon,
  Visibility as VisibilityIcon,
  Hearing as HearingIcon,
  OpenInNew as OpenInNewIcon,
  Speed as SpeedIcon,
  Shield as ShieldIcon,
  CloudDone as CloudDoneIcon,
  TrendingUp as TrendingUpIcon,
  LockOpen as LockOpenIcon
} from '@mui/icons-material';

const Section508Compliant: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = React.useState(3);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const urls = [
      '/capabilities/high-performance',
      '/capabilities/zero-trust-security',
      '/capabilities/fedramp-ready',
      '/capabilities/section-508-compliant',
      '/capabilities/scalable-architecture',
      '/capabilities/no-vendor-lock-in'
    ];
    navigate(urls[newValue]);
  };
  
  // Section508.gov official color palette
  const primaryColor = '#0076D6'; // Official Section508.gov Blue
  const accentColor = '#005EA2'; // Darker blue for hover states
  const redAccent = '#E52207'; // Section 508 Program Red
  const greenAccent = '#538200'; // Playbooks Green

  const benefits = [
    'Full WCAG 2.0 Level AA compliance for all user interfaces',
    'Screen reader compatibility with ARIA labels and landmarks',
    'Complete keyboard navigation without mouse dependency',
    'High contrast modes and customizable text sizing',
    'Captions and transcripts for all multimedia content',
    'Regular accessibility audits and user testing with assistive technologies'
  ];

  const capabilities = [
    {
      icon: <AccessibilityIcon sx={{ color: primaryColor }} />,
      title: 'Universal Design',
      description: 'Inclusive design principles ensure usability for all users regardless of abilities',
      color: primaryColor
    },
    {
      icon: <KeyboardIcon sx={{ color: greenAccent }} />,
      title: 'Keyboard Navigation',
      description: 'Full functionality accessible via keyboard with visible focus indicators',
      color: greenAccent
    },
    {
      icon: <VisibilityIcon sx={{ color: redAccent }} />,
      title: 'Visual Accessibility',
      description: 'Proper contrast ratios, resizable text, and alternative visual presentations',
      color: redAccent
    },
    {
      icon: <HearingIcon sx={{ color: primaryColor }} />,
      title: 'Assistive Technology',
      description: 'Compatible with screen readers, voice recognition, and other assistive tools',
      color: primaryColor
    }
  ];

  const useCases = [
    {
      title: 'Federal Workforce Inclusion',
      description: 'Enable all federal employees, including those with disabilities, to access and use the platform effectively'
    },
    {
      title: 'Legal Compliance',
      description: 'Meet Section 508 requirements and avoid legal risks associated with inaccessible technology'
    },
    {
      title: 'Enhanced Usability',
      description: 'Accessibility improvements benefit all users through clearer navigation and better design'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3, color: 'primary.main' }}
      >
        Back to About Page
      </Button>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label="Core Capability tabs"
        variant="scrollable"
        scrollButtons="auto"
        sx={{ 
          mb: 4,
          '.MuiTab-root': {
            color: 'primary.main',
            minHeight: 64,
            '&.Mui-selected': {
              color: 'primary.main',
              fontWeight: 700
            }
          },
          '.MuiTabs-indicator': {
            backgroundColor: '#0076D6',
            height: 3
          }
        }}
      >
        <Tab label="High Performance" icon={<SpeedIcon />} iconPosition="start" />
        <Tab label="Zero Trust" icon={<ShieldIcon />} iconPosition="start" />
        <Tab label="FedRAMP Ready" icon={<CloudDoneIcon />} iconPosition="start" />
        <Tab label="508 Compliant" icon={<AccessibilityIcon />} iconPosition="start" />
        <Tab label="Scalable" icon={<TrendingUpIcon />} iconPosition="start" />
        <Tab label="No Lock-In" icon={<LockOpenIcon />} iconPosition="start" />
      </Tabs>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AccessibilityIcon sx={{ fontSize: 48, color: primaryColor, mr: 2 }} />
          <Typography variant="h3" sx={{ color: primaryColor, fontWeight: 'bold' }}>
            508 Compliant
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Full Section 508 and WCAG 2.0 Level AA accessibility compliance
        </Typography>
      </Box>

      <Paper sx={{ p: 4, mb: 4, borderLeft: `4px solid ${primaryColor}` }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom sx={{ color: primaryColor, fontWeight: 600 }}>
              Overview
            </Typography>
            <Typography paragraph sx={{ fontSize: '0.95rem' }}>
              E-Unify is designed with accessibility at its core, ensuring that all users, regardless of their abilities, can effectively access and use the platform. Our commitment to Section 508 compliance and WCAG 2.0 Level AA standards means that federal agencies can confidently deploy E-Unify knowing it meets all accessibility requirements.
            </Typography>
            <Typography sx={{ fontSize: '0.95rem' }}>
              We go beyond minimum compliance by incorporating universal design principles, conducting regular accessibility audits, and testing with real users who rely on assistive technologies. This approach ensures that accessibility is not an afterthought but a fundamental aspect of the platform.
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom sx={{ color: primaryColor, fontWeight: 600 }}>
              Key Benefits
            </Typography>
            <List dense>
              {benefits.map((benefit, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircleIcon sx={{ color: primaryColor, fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={benefit}
                    primaryTypographyProps={{ fontSize: '0.95rem' }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </Paper>

      {/* DHS-Specific Section 508 Commitments */}
      <Paper sx={{ p: 4, mb: 4, borderLeft: `4px solid ${redAccent}`, backgroundColor: '#FFF5F5' }}>
        <Typography variant="h5" gutterBottom sx={{ color: redAccent, fontWeight: 600 }}>
          E-Unify's Section 508 Commitments for DHS
        </Typography>
        <Typography paragraph sx={{ fontWeight: 500 }}>
          As a metadata management platform designed for the Department of Homeland Security, E-Unify will deliver comprehensive Section 508 compliance across all components:
        </Typography>
        
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 1, height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ color: primaryColor, fontWeight: 600 }}>
                Accessibility Testing & Validation
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon sx={{ color: greenAccent, fontSize: 20 }} /></ListItemIcon>
                  <ListItemText 
                    primary="Automated accessibility scanning with industry-leading tools"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon sx={{ color: greenAccent, fontSize: 20 }} /></ListItemIcon>
                  <ListItemText 
                    primary="Manual testing with screen readers (JAWS, NVDA, VoiceOver)"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon sx={{ color: greenAccent, fontSize: 20 }} /></ListItemIcon>
                  <ListItemText 
                    primary="Keyboard-only navigation testing for all functionality"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon sx={{ color: greenAccent, fontSize: 20 }} /></ListItemIcon>
                  <ListItemText 
                    primary="Color contrast verification meeting WCAG 2.0 Level AA standards"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              </List>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 1, height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ color: primaryColor, fontWeight: 600 }}>
                Documentation & Reporting
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon sx={{ color: greenAccent, fontSize: 20 }} /></ListItemIcon>
                  <ListItemText 
                    primary="Accessibility Conformance Report (ACR) using VPAT® template"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon sx={{ color: greenAccent, fontSize: 20 }} /></ListItemIcon>
                  <ListItemText 
                    primary="Detailed test results and remediation plans"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon sx={{ color: greenAccent, fontSize: 20 }} /></ListItemIcon>
                  <ListItemText 
                    primary="User guides with accessibility features documentation"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon sx={{ color: greenAccent, fontSize: 20 }} /></ListItemIcon>
                  <ListItemText 
                    primary="Quarterly accessibility compliance status reports"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              </List>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 1, height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ color: primaryColor, fontWeight: 600 }}>
                Training & Support
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon sx={{ color: greenAccent, fontSize: 20 }} /></ListItemIcon>
                  <ListItemText 
                    primary="Accessibility training for DHS users and administrators"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon sx={{ color: greenAccent, fontSize: 20 }} /></ListItemIcon>
                  <ListItemText 
                    primary="Assistive technology compatibility guidance"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon sx={{ color: greenAccent, fontSize: 20 }} /></ListItemIcon>
                  <ListItemText 
                    primary="Dedicated accessibility support channel"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon sx={{ color: greenAccent, fontSize: 20 }} /></ListItemIcon>
                  <ListItemText 
                    primary="Best practices documentation for content creators"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              </List>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 1, height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ color: primaryColor, fontWeight: 600 }}>
                Continuous Compliance
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon sx={{ color: greenAccent, fontSize: 20 }} /></ListItemIcon>
                  <ListItemText 
                    primary="Automated accessibility regression testing in CI/CD pipeline"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon sx={{ color: greenAccent, fontSize: 20 }} /></ListItemIcon>
                  <ListItemText 
                    primary="Regular third-party accessibility audits"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon sx={{ color: greenAccent, fontSize: 20 }} /></ListItemIcon>
                  <ListItemText 
                    primary="Accessibility issue tracking and remediation workflow"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon sx={{ color: greenAccent, fontSize: 20 }} /></ListItemIcon>
                  <ListItemText 
                    primary="Compliance monitoring dashboard for DHS stakeholders"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              </List>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, p: 2, backgroundColor: 'white', borderRadius: 1, borderLeft: `3px solid ${greenAccent}` }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: greenAccent, mb: 1 }}>
            ✓ DHS Compliance Guarantee
          </Typography>
          <Typography variant="body2">
            E-Unify commits to maintaining full Section 508 compliance throughout the platform lifecycle, ensuring that all DHS components and users can access and utilize the metadata management capabilities without barriers. We will work closely with DHS Section 508 Program Managers to ensure alignment with agency-specific accessibility requirements and policies.
          </Typography>
        </Box>
      </Paper>


      <Typography variant="h5" gutterBottom sx={{ color: primaryColor, fontWeight: 600, mb: 3 }}>
        Accessibility Features
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {capabilities.map((capability, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ height: '100%', borderLeft: `4px solid ${capability.color}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {capability.icon}
                  <Typography variant="h6" sx={{ ml: 1, fontWeight: 600, color: capability.color }}>
                    {capability.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {capability.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h5" gutterBottom sx={{ color: primaryColor, fontWeight: 600, mb: 3 }}>
        Common Use Cases
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {useCases.map((useCase, index) => (
          <Grid item xs={12} key={index}>
            <Paper sx={{ p: 3, borderLeft: `4px solid ${primaryColor}` }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {useCase.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {useCase.description}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Resources Section */}
      <Typography variant="h5" gutterBottom sx={{ color: primaryColor, fontWeight: 600, mb: 3, mt: 4 }}>
        Section 508 Resources
      </Typography>
      <Paper sx={{ p: 4, mb: 4, borderLeft: `4px solid ${primaryColor}`, backgroundColor: '#F0F0F0' }}>
        <Typography variant="body1" paragraph>
          Learn more about Section 508 compliance and accessibility standards:
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Button
              variant="outlined"
              fullWidth
              href="https://www.section508.gov/"
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon />}
              sx={{
                justifyContent: 'space-between',
                borderColor: primaryColor,
                color: primaryColor,
                textAlign: 'left',
                py: 2,
                backgroundColor: 'white',
                '&:hover': {
                  borderColor: accentColor,
                  backgroundColor: 'rgba(0, 94, 162, 0.04)'
                }
              }}
            >
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Section508.gov - Official Website
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The official U.S. government resource for Section 508 compliance
                </Typography>
              </Box>
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="outlined"
              fullWidth
              href="https://www.w3.org/WAI/WCAG21/quickref/"
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon />}
              sx={{
                justifyContent: 'space-between',
                borderColor: primaryColor,
                color: primaryColor,
                textAlign: 'left',
                py: 2,
                backgroundColor: 'white',
                '&:hover': {
                  borderColor: accentColor,
                  backgroundColor: 'rgba(0, 94, 162, 0.04)'
                }
              }}
            >
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  WCAG 2.1 Quick Reference
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Web Content Accessibility Guidelines from W3C
                </Typography>
              </Box>
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="outlined"
              fullWidth
              href="https://www.access-board.gov/ict/"
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon />}
              sx={{
                justifyContent: 'space-between',
                borderColor: primaryColor,
                color: primaryColor,
                textAlign: 'left',
                py: 2,
                backgroundColor: 'white',
                '&:hover': {
                  borderColor: accentColor,
                  backgroundColor: 'rgba(0, 94, 162, 0.04)'
                }
              }}
            >
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  U.S. Access Board - ICT Standards
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Information and Communication Technology accessibility standards
                </Typography>
              </Box>
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper 
        sx={{ 
          p: 4, 
          textAlign: 'center', 
          backgroundColor: primaryColor,
          color: 'white'
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Accessibility for Everyone
        </Typography>
        <Typography variant="body1" paragraph>
          Learn how E-Unify ensures equal access for all users
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/contact')}
          sx={{
            bgcolor: 'white',
            color: primaryColor,
            '&:hover': {
              bgcolor: '#f0f0f0',
              color: accentColor
            }
          }}
        >
          Contact Us
        </Button>
      </Paper>
    </Container>
  );
};

export default Section508Compliant;
